import { Controller } from "@hotwired/stimulus"
import { formatCurrency } from "../modules/salary_lab/formatter"
import { trackCalculatorUsed } from "../modules/salary_lab/ga4_tracker"

/**
 * Stimulus controller for SalaryLab benchmark widget.
 * Two modes:
 * 1. Server-rendered: percentiles pre-loaded via data values, updates marker on salary change
 * 2. AJAX: fetches benchmark data when role is selected on calculator page
 */
export default class extends Controller {
  static targets = [
    "marker", "rankValue", "comparisonText", "medianValue",
    "barContainer", "container", "tickMarks"
  ]
  static values = {
    percentiles: Object,
    visible: Array,
    salary: Number,
    rank: Number,
    roleSlug: String,
    benchmarkUrl: { type: String, default: "/benchmark" },
  }

  connect() {
    this._animationFrame = null
    this._fetchController = null
  }

  disconnect() {
    if (this._animationFrame) cancelAnimationFrame(this._animationFrame)
    if (this._fetchController) this._fetchController.abort()
  }

  /**
   * Called when calculator emits a calculated event.
   * In AJAX mode (role selected), always re-fetch so the server can
   * re-detect the best level for the new salary.
   */
  updateSalary({ detail }) {
    if (!detail) return
    const salary = detail.salary || detail.gross_salary
    if (!salary || salary <= 0) return

    this.salaryValue = salary

    if (this.roleSlugValue) {
      this._fetchBenchmark(salary, this.roleSlugValue)
    } else if (this.hasPercentileData()) {
      this._updateDisplay()
    }
  }

  /**
   * Called when a role is selected (from role selector controller).
   */
  roleSelected({ detail }) {
    if (!detail?.slug) return
    this.roleSlugValue = detail.slug

    if (this.salaryValue > 0) {
      this._fetchBenchmark(this.salaryValue, detail.slug)
    }
  }

  /**
   * Called when role is cleared.
   */
  roleCleared() {
    this.roleSlugValue = ""
    this.percentilesValue = {}
    this._hide()
    this.dispatch("cleared", { bubbles: true })
  }

  salaryValueChanged() {
    if (!this.roleSlugValue && this.hasPercentileData()) {
      this._updateDisplay()
    }
  }

  async _fetchBenchmark(salary, roleSlug) {
    if (this._fetchController) this._fetchController.abort()
    this._fetchController = new AbortController()

    try {
      const response = await fetch(this.benchmarkUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this._csrfToken(),
        },
        body: JSON.stringify({ salary, role_slug: roleSlug }),
        signal: this._fetchController.signal,
      })

      if (!response.ok) return

      const data = await response.json()
      if (!data.has_data) {
        this._hide()
        return
      }

      this.percentilesValue = data.percentiles
      this.visibleValue = data.visible_percentiles || []
      this.rankValue = data.percentile_rank
      this.salaryValue = salary

      this._updateDisplay()
      this._updateComparisonFromServer(data)
      this._updateMedian(data.median)
      this._show()
      trackCalculatorUsed("benchmark", { salaryAmount: salary })

      this.dispatch("updated", {
        detail: {
          role_name: data.role_name,
          level_name: data.level_name,
          median: data.median,
          next_level: data.next_level
        },
        bubbles: true
      })
    } catch (e) {
      if (e.name !== "AbortError") console.error("Benchmark fetch failed:", e)
    }
  }

  _updateDisplay() {
    const salary = this.salaryValue
    if (!salary || !this.hasPercentileData()) return

    const rank = this._bestAvailableRank(salary)
    this.rankValue = rank

    if (this.hasRankValueTarget) {
      this.rankValueTarget.textContent = rank
    }

    if (this.hasMarkerTarget) {
      const position = this._rankToPosition(rank)
      this.markerTarget.style.left = `${position}%`
      this.markerTarget.setAttribute("aria-label", `Your salary at the ${rank} percentile`)
    }

    if (this.hasComparisonTextTarget) {
      this.comparisonTextTarget.textContent = this._buildComparisonText(salary, rank)
    }
  }

  _updateComparisonFromServer(data) {
    if (this.hasComparisonTextTarget && data.comparison_text) {
      this.comparisonTextTarget.textContent = data.comparison_text
    }
  }

  _updateMedian(median) {
    if (this.hasMedianValueTarget && median) {
      this.medianValueTarget.textContent = formatCurrency(median)
    }
  }

  _show() {
    if (this.hasContainerTarget) {
      this.containerTarget.classList.remove("hidden")
    }
  }

  _hide() {
    if (this.hasContainerTarget) {
      this.containerTarget.classList.add("hidden")
    }
  }

  hasPercentileData() {
    return this.percentilesValue && Object.keys(this.percentilesValue).length > 0
  }

  /**
   * Use server-provided rank when local percentile data is too sparse
   * to interpolate accurately (fewer than 2 data points).
   */
  _bestAvailableRank(salary) {
    const points = Object.keys(this.percentilesValue)
    if (points.length >= 2) {
      return this._estimatePercentileRank(salary)
    }
    if (this.rankValue > 0) {
      return this.rankValue
    }
    return this._estimatePercentileRank(salary)
  }

  _estimatePercentileRank(salary) {
    const percentiles = this.percentilesValue
    const points = Object.entries(percentiles)
      .map(([key, val]) => [parseInt(key.replace("percentile_", "")), val])
      .sort((a, b) => a[0] - b[0])

    if (points.length === 0) return 50

    if (salary <= points[0][1]) return points[0][0]
    if (salary >= points[points.length - 1][1]) return points[points.length - 1][0]

    for (let i = 0; i < points.length - 1; i++) {
      const [lowerPct, lowerVal] = points[i]
      const [upperPct, upperVal] = points[i + 1]

      if (salary >= lowerVal && salary <= upperVal) {
        const range = upperVal - lowerVal
        if (range === 0) return lowerPct
        const fraction = (salary - lowerVal) / range
        return Math.round(lowerPct + fraction * (upperPct - lowerPct))
      }
    }

    return 50
  }

  _rankToPosition(rank) {
    return Math.max(2, Math.min(98, rank))
  }

  _buildComparisonText(salary, rank) {
    const percentiles = this.percentilesValue
    const median = percentiles.percentile_50
    if (!median) return ""

    const diff = salary - median
    const absDiff = Math.abs(diff)
    const pct = ((absDiff / median) * 100).toFixed(1)

    if (diff === 0) return "Your salary matches the median for this role."
    if (parseFloat(pct) < 5) return "Your salary is close to the median for this role."

    const direction = diff > 0 ? "above" : "below"
    return `Your salary is ${formatCurrency(absDiff)} (${pct}%) ${direction} the median for this role.`
  }

  _csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
